import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  SalesBriefingSection,
  StickyCtaBar,
  BriefingApplicationModal,
  FranchiseInquiryModal,
} from '@marketing/components'
import { useHashScroll } from '@/hooks/useHashScroll'
import { AboutQuebonHero } from '@/components/home/about-quebon/AboutQuebonHero'
import { PhilosophySection } from '@/components/home/about-quebon/PhilosophySection'
import { StorySection } from '@/components/home/about-quebon/StorySection'
import { ProfileSection } from '@/components/home/about-quebon/ProfileSection'
import { TechnologySection } from '@/components/home/about-quebon/TechnologySection'
import { PatentsSection } from '@/components/home/about-quebon/PatentsSection'
import { MediaSection } from '@/components/home/about-quebon/MediaSection'
import { getAboutQuebonContent } from './aboutQuebon.content'

export default function AboutQuebonPage() {
  // Rebuild copy on language changes; asset URLs and business values stay unchanged.
  const { t: mt } = useTranslation('marketing')
  const c = getAboutQuebonContent(mt)

  const navigate = useNavigate()
  const [briefingOpen, setBriefingOpen] = useState(false)
  const [franchiseOpen, setFranchiseOpen] = useState(false)

  const handleBriefing = () => setBriefingOpen(true)
  const handleInquiry = () => setFranchiseOpen(true)

  useHashScroll()

  return (
    <>
      {/* #01 Hero — text only (Figma 1:1) */}
      <AboutQuebonHero
        label={c.hero.label}
        subtitle={c.hero.subtitle}
        title={c.hero.title}
        description={c.hero.description}
        descriptionHighlight={c.hero.descriptionHighlight}
      />

      {/* #02 Philosophy — composite illustration + 4 cards (Figma 1:1) */}
      <PhilosophySection
        title={c.philosophy.title}
        cards={c.philosophy.cards}
        imageUrl="/marketing/about-quebon/02-img01.webp"
        imageUrlMobile="/marketing/about-quebon/02-img01-m.webp"
        imageAlt={mt('AboutQuebonPage.imageAlt_337')}
      />

      {/* #03 Story — 2 text blocks with full-bleed image band between (Figma 1:1 from legacy) */}
      <StorySection
        blocks={c.story.blocks}
        imageUrl={c.story.imageUrl}
        imageUrlMobile={c.story.imageUrlMobile}
        imageAlt={c.story.imageAlt}
      />

      {/* #04 Profile — Cho Bong-han (Figma 1:1 from legacy) */}
      <ProfileSection
        label={c.founder.label}
        title={c.founder.title}
        sections={c.founder.sections}
        imageUrlXl={c.founder.imageUrlXl}
        imageUrlLg={c.founder.imageUrlLg}
        imageUrlMd={c.founder.imageUrlMd}
        imageUrlSm={c.founder.imageUrlSm}
        imageAlt={c.founder.imageAlt}
      />

      {/* #05 Media — KBS/MBC/SBS video tabs + dynamic news list (Figma 1:1 from legacy) */}
      <MediaSection
        title={c.media.title}
        titleHighlight={c.media.titleHighlight}
        videos={c.media.videos}
        moreLabel={c.media.moreLabel}
        moreUrl={c.media.moreUrl}
      />

      {/* #06 Patents — dark starfield + 3D flip cards (Figma 1:1 from legacy) */}
      <PatentsSection
        title={c.patents.title}
        titleHighlight={c.patents.titleHighlight}
        description={c.patents.description}
        notice={c.patents.notice}
        backgroundImageUrl={c.patents.backgroundImageUrl}
        cardBackImageUrl={c.patents.cardBackImageUrl}
        flagIconUrl={c.patents.flagIconUrl}
        cards={c.patents.cards}
      />

      {/* #07 Technology — title + laurel flags + patent marquee (Figma 1:1 from legacy) */}
      <TechnologySection
        title={c.technology.title}
        titleHighlight={c.technology.titleHighlight}
        description={c.technology.description}
        patentNotice={c.technology.patentNotice}
        patents={c.technology.patents}
      />

      {/* #08 Sales briefing — shared */}
      <SalesBriefingSection
        onBriefingClick={handleBriefing}
        onInquiryClick={() => navigate('/franchise')}
      />

      <StickyCtaBar
        onBriefingClick={handleBriefing}
        onInquiryClick={handleInquiry}
      />

      <BriefingApplicationModal open={briefingOpen} onClose={() => setBriefingOpen(false)} />
      <FranchiseInquiryModal open={franchiseOpen} onClose={() => setFranchiseOpen(false)} />
    </>
  )
}
